import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { panel } from "../motion";
import { hkToday } from "../lib/dates";
import { IconChevronLeft, IconChevronRight } from "./icons";
import { fmtCell, MONTHS, monthCells, WEEKDAYS } from "./calendar-grid";

interface MiniCalendarProps {
  open: boolean;
  /** Currently selected day (YYYY-MM-DD); the calendar opens on its month. */
  date: string;
  onSelect: (date: string) => void;
  onClose: () => void;
}

/**
 * Popover mini calendar that opens under the hero date button and lets the OT
 * jump straight to any day's notes. Same popover language as the app's other
 * pickers (panel motion, outside-click/Escape close); adjacent-month days are
 * shown muted and are still clickable, so browsing across a month boundary is
 * one click instead of a month-nav detour.
 */
export function MiniCalendar({ open, date, onSelect, onClose }: MiniCalendarProps) {
  const [y, m] = date.split("-").map(Number);
  const [view, setView] = useState(() => ({ year: y!, month: m! - 1 }));
  const rootRef = useRef<HTMLDivElement>(null);
  const [shiftLeft, setShiftLeft] = useState(0);

  // Each time the picker opens, show the selected day's month.
  useEffect(() => {
    if (open) setView({ year: y!, month: m! - 1 });
  }, [open, y, m]);

  // Close on outside click / Escape, matching the other dropdowns in the app.
  useEffect(() => {
    if (!open) return;
    function onDocDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // The popover is anchored under the date button (left: 0), but on narrow
  // screens that would push it past the viewport right edge — shift it left so
  // it always stays on screen (16px gutter). Re-measure on resize while open.
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const wrap = rootRef.current?.parentElement;
      if (!wrap) return;
      const r = wrap.getBoundingClientRect();
      const overflow = r.left + 288 + 16 - window.innerWidth;
      setShiftLeft(overflow > 0 ? -overflow : 0);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);

  const today = hkToday();
  const cells = monthCells(view.year, view.month);

  const shiftMonth = (delta: number) =>
    setView((v) => {
      const month = v.month + delta;
      if (month < 0) return { year: v.year - 1, month: 11 };
      if (month > 11) return { year: v.year + 1, month: 0 };
      return { ...v, month };
    });

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          ref={rootRef}
          className="mini-cal"
          role="dialog"
          aria-label="Choose a day"
          style={{ left: shiftLeft }}
          variants={panel}
          initial="hidden"
          animate="show"
          exit="exit"
        >
          <div className="mini-cal-head">
            <button type="button" className="mini-cal-nav" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
              <IconChevronLeft />
            </button>
            <div className="mini-cal-title" aria-live="polite">
              {MONTHS[view.month]} {view.year}
            </div>
            <button type="button" className="mini-cal-nav" aria-label="Next month" onClick={() => shiftMonth(1)}>
              <IconChevronRight />
            </button>
          </div>
          <div className="mini-cal-grid">
            {WEEKDAYS.map((w) => (
              <div key={w} className="mini-cal-dow">
                {w}
              </div>
            ))}
            {cells.map((c) => (
              <button
                key={c.key}
                type="button"
                className={`mini-cal-day${c.out ? " out" : ""}${c.date === date ? " selected" : ""}${c.date === today ? " today" : ""}`}
                aria-label={fmtCell(c.date)}
                aria-pressed={c.date === date}
                onClick={() => onSelect(c.date)}
              >
                {c.day}
              </button>
            ))}
          </div>
          <div className="mini-cal-foot">
            <button type="button" className="mini-cal-today" onClick={() => onSelect(today)}>
              Today
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
